/**
 * PlatformaPage — platform administration (`/platforma`), for platform admins
 * (`uzivatele.admin`) only. Deliberately separate from Správa firmy
 * (`/management`): that area is the active company's own administration and
 * looks the same to a company admin (správce) and to a platform admin; this
 * one spans every company.
 *   `?tab=firmy` (default) — companies, their modules, LMS locations, members
 *   `?tab=uzivatele`       — user accounts, LMS ID + LMS locations, memberships
 *   `?tab=ulohy`           — scheduled-job log
 *   `?tab=pujcovna`        — rental module: the shared web categories
 *   `?tab=diagnostika`     — the network path of the admin's own request (proxy addresses and headers)
 *
 * Not a company module (never in firma_moduly / Členové): the rail item and
 * this page are gated on the platform-admin flag alone. The API behind every
 * tab is platform-admin-only server-side; the UI gate only mirrors it.
 */

import { useNavigate, useSearch } from '@tanstack/react-router';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Shell } from '@/components/Shell';
import { Subtabs } from '@/components/Subtabs';
import { useModuleAccess } from '@/lib/permissions';
import { CronLogsWorkspace } from '@/modules/management/CronLogsWorkspace';
import { FirmyWorkspace } from '@/modules/management/FirmyWorkspace';
import { UzivateleWorkspace } from '@/modules/management/UzivateleWorkspace';
import { KategorieWorkspace } from '@/modules/pujcovna/KategorieWorkspace';
import { EdgeDiagnosticsWorkspace } from './EdgeDiagnosticsWorkspace';
import type { PlatformaSearch, PlatformaTab } from './helpers';

const TABS: { value: PlatformaTab; label: string }[] = [
  { value: 'firmy', label: 'Firmy' },
  { value: 'uzivatele', label: 'Uživatelé' },
  { value: 'ulohy', label: 'Záznamy úloh' },
  { value: 'pujcovna', label: 'Půjčovna' },
  { value: 'diagnostika', label: 'Diagnostika' },
];

const HEADINGS: Record<PlatformaTab, [string, string]> = {
  firmy: ['Firmy', 'správa subjektů.'],
  uzivatele: ['Uživatelé', 'účty, LMS a členství.'],
  ulohy: ['Záznamy úloh', 'naplánované běhy.'],
  pujcovna: ['Půjčovna', 'kategorie vozidel.'],
  diagnostika: ['Diagnostika', 'cesta požadavku.'],
};

export function PlatformaPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/platforma' }) as PlatformaSearch;
  const activeTab: PlatformaTab = search.tab ?? 'firmy';
  const { isAdmin, resolved } = useModuleAccess();

  const onTabChange = (next: PlatformaTab) => {
    if (next === activeTab) return;
    navigate({ to: '/platforma', search: next === 'firmy' ? {} : { tab: next }, replace: true });
  };

  const [title, subtitle] = HEADINGS[activeTab];

  return (
    <Shell rail={<AppRail active="platforma" />}>
      <Crown>
        <Breadcrumb
          segments={[
            { label: 'Platforma' },
            { label: TABS.find((t) => t.value === activeTab)?.label ?? '' },
          ]}
        />
        <Crown.Row className="mt-3">
          <DisplayHeading>
            {title},
            <br />
            <em>{subtitle}</em>
          </DisplayHeading>
          <CrownMeta items={[{ label: 'Rozsah', value: 'Všechny firmy' }]} />
        </Crown.Row>
        {isAdmin && (
          <Subtabs<PlatformaTab>
            aria-label="Platforma — sekce"
            value={activeTab}
            onChange={onTabChange}
            items={TABS}
          />
        )}
      </Crown>

      {resolved && !isAdmin ? (
        <div
          data-testid="platforma-403"
          className="flex min-h-0 flex-1 flex-col px-8 pb-8 pt-5"
        >
          <EmptyState
            eyebrow="403"
            title="Pouze pro administrátora platformy"
            description="Firmy, uživatelské účty a nastavení LMS spravuje administrátor platformy. Správu vaší firmy najdete v sekci Správa firmy."
          />
        </div>
      ) : activeTab === 'uzivatele' ? (
        <UzivateleWorkspace isAdmin={isAdmin} />
      ) : activeTab === 'ulohy' ? (
        <CronLogsWorkspace isAdmin={isAdmin} />
      ) : activeTab === 'pujcovna' ? (
        <KategorieWorkspace isAdmin={isAdmin} />
      ) : activeTab === 'diagnostika' ? (
        <EdgeDiagnosticsWorkspace isAdmin={isAdmin} />
      ) : (
        <FirmyWorkspace isAdmin={isAdmin} />
      )}
    </Shell>
  );
}
